import * as React from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon } from "lucide-react";

import type { components } from "@/api/schema";
import { useSegments } from "@/api/segments";
import { useEntitySelection } from "@/components/entity-select/useEntitySelection";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

import { Pill } from "./Pill";

type Segment = components["schemas"]["Segment"];

export type AcrossPillProps = {
  value: number[];
  onChange: (next: number[]) => void;
};

export function AcrossPill({ value, onChange }: AcrossPillProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");

  const sel = useEntitySelection<Segment>({
    value,
    query,
    useSearchResults: (q) => {
      const { data, isLoading } = useSegments();
      const all = (data as Segment[] | undefined) ?? [];
      const list = q
        ? all.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()))
        : all;
      return { data: list, isLoading };
    },
    useResolveSelected: (ids) => {
      const { data } = useSegments();
      const all = (data as Segment[] | undefined) ?? [];
      return { data: all.filter((s) => ids.includes(s.id)) };
    },
    getId: (s) => s.id,
  });

  const isDefault = value.length === 0;
  const label = isDefault
    ? t("demand.pills.across.allSegments")
    : value.length === 1
      ? (sel.selectedEntities[0]?.name ??
        t("demand.pills.across.segment", { count: 1 }))
      : t("demand.pills.across.segment", { count: value.length });

  function toggle(id: number) {
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
    );
  }

  return (
    <Pill
      label={label}
      isDefault={isDefault}
      onClear={isDefault ? undefined : () => onChange([])}
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={t("demand.pills.across.searchPlaceholder")}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {sel.isLoading && (
            <div className="text-muted-foreground p-2 text-sm">
              {t("common.loading")}
            </div>
          )}
          <CommandEmpty>{t("demand.pills.noMatches")}</CommandEmpty>
          {sel.selectedEntities.length > 0 && (
            <>
              <CommandGroup
                heading={t("components.entitySelect.selectedHeading")}
              >
                {sel.selectedEntities.map((s) => (
                  <CommandItem
                    key={`sel-${s.id}`}
                    value={`sel-${s.name}-${s.id}`}
                    onSelect={() => toggle(s.id)}
                  >
                    {s.name}
                    <CheckIcon className="ml-auto size-4 opacity-100" />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}
          <CommandGroup>
            {sel.results.map((s) => (
              <CommandItem
                key={s.id}
                value={`${s.name}-${s.id}`}
                onSelect={() => toggle(s.id)}
              >
                {s.name}
                <CheckIcon className="ml-auto size-4 opacity-0" />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </Pill>
  );
}
